// Who may do what on the map (account/MapGate.js), without asking the server anything.
// The map's data servlets also limit guests to a few places and layers per session
// (util/GuestLimits.java), refusing past them with the same prompt (onGuestLimit in serverApi.js).
//   guest:      not logged in. The map opens on a link's place and layers, and they can try
//               FREE_LAYER_SWITCHES other layers (counted in the browser), then must log in.
//               They can also move to FREE_SEARCHES other places (a search or a dropped pin,
//               counted in the browser; a place tried once is free again), then must log in.
//               The server keeps its own, looser count (util/GuestLimits.java).
//   free:       logged in without a subscription. The whole map, except LOCKED_GRAPHS, which
//               a single report unlocks for its home's output area (reportAreas, from
//               /savedlistings status) while it is unlocked; the server checks the same
//               (util/GraphAccess.java).
//   subscriber: everything.
// Neither guests nor free accounts get the area overview opened for them, and when they open
// it they see the teaser (overviewTeaser.js) rather than its tabs.
import { OPEN_MAP_LAYERS } from "./mapPaywallRules";

export const FREE_LAYER_SWITCHES = 3;

// Other places a guest may search for or pick, past the one the map opened on
export const FREE_SEARCHES = 3;

// The price and crime graphs, for subscribers, and for a bought report's own area
export const LOCKED_GRAPHS = ['salesGraph', 'crimeGraph'];

// Layers for subscribers only, for guests and free accounts alike: Social housing and Future
// developments. Turning one on opens the subscribe prompt instead (MapGate.js)
export const LOCKED_LAYERS = ['socialHousing', 'futureDevelopments'];
// The locked layer being added (in next but not prev), or null
export const lockedLayerAdded = (tier, prev = [], next = []) => !tier || tier === 'subscriber' ? null
    : (next || []).find((l) => LOCKED_LAYERS.includes(l) && !(prev || []).includes(l)) || null;

// The graph layer each locked server function draws (util/GraphAccess.java's LOCKED_FNS)
export const GRAPH_OF_FN = {
    property_sales_timeseries2:'salesGraph',
    property_sales_timeseries:'salesGraph',
    street_crime_timeseries:'crimeGraph',
};

// Whether a free account's single reports unlock the graphs at the output area oacd. With the
// area not known yet, any report lets the graph try, and the server decides.
export const graphsUnlocked = (reportAreas = [], oacd) =>
    reportAreas.length > 0 && (!oacd || reportAreas.includes(oacd));

// Layers a guest turns on without using up a switch: the Big Picture's maps (open to everyone
// before the map was) and the local area itself
const UNCOUNTED = [...OPEN_MAP_LAYERS, 'pointCircle'];

export const accessTier = (access, user) => {
    if (access && !access.error) return access.signedin ? (access.subscribed ? 'subscriber' : 'free') : 'guest';
    return user && user.signedin ? 'free' : 'guest';
};

const STORE_KEY = 'tms.mapLayersTried';

// The layers a guest has tried in this browser, as a list of names
export const triedLayers = () => {
    try {
        const list = JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
        return Array.isArray(list) ? list.filter((l) => typeof l === 'string').slice(0, 50) : [];
    } catch (e) { return []; }
};

export const saveTriedLayers = (list) => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(list)); } catch (e) { /* storage blocked: the count is lost on reload */ }
};

// Whether a change of the map's layers from prev to next may go ahead:
// {allow, prompt} where prompt is {kind:'graph', layer, report} or {kind:'layers'} when it may
// not (report: they have single reports, just not for this place), and tried is the guest's
// new list of tried layers when it changed.
// A layer tried once can be turned on again for free; so can the link's own.
// reportAreas and oacd: the areas a free account's reports unlock, and the map's area now.
export const judgeLayers = ({tier, prev, next, linkLayers = [], tried = [], reportAreas = [], oacd}) => {
    const added = next.filter((l) => !prev.includes(l));
    if (!added.length || tier === 'subscriber') return {allow:true};
    const graph = added.find((l) => LOCKED_GRAPHS.includes(l));
    if (graph && !(tier === 'free' && graphsUnlocked(reportAreas, oacd))) {
        return {allow:false, prompt:tier === 'free' && reportAreas.length
            ? {kind:'graph', layer:graph, report:true} : {kind:'graph', layer:graph}};
    }
    if (tier !== 'guest') return {allow:true};
    const fresh = added.filter((l) => !UNCOUNTED.includes(l) && !linkLayers.includes(l) && !tried.includes(l));
    if (!fresh.length) return {allow:true};
    if (tried.length + fresh.length > FREE_LAYER_SWITCHES) return {allow:false, prompt:{kind:'layers'}};
    return {allow:true, tried:[...tried, ...fresh]};
};

const samePoint = (a, b) => !!(a && b) && Math.round(a.x) === Math.round(b.x) && Math.round(a.y) === Math.round(b.y);

const PLACES_KEY = 'tms.mapPlacesTried';
const pointKey = (p) => Math.round(p.x) + ',' + Math.round(p.y);

// The places a guest has moved to in this browser, as "x,y" keys
export const triedPlaces = () => {
    try {
        const list = JSON.parse(localStorage.getItem(PLACES_KEY) || '[]');
        return Array.isArray(list) ? list.filter((l) => typeof l === 'string').slice(0, 50) : [];
    } catch (e) { return []; }
};

export const saveTriedPlaces = (list) => {
    try { localStorage.setItem(PLACES_KEY, JSON.stringify(list)); } catch (e) { /* storage blocked: the count is lost on reload */ }
};

// Searches a guest has left
export const searchesLeft = (tried = []) => Math.max(0, FREE_SEARCHES - tried.length);

// Whether the map may move to a new place: a guest may move to FREE_SEARCHES other places
// than the one they opened (one tried before is free again), then is asked to sign up.
// tried is the guest's new list when it changed.
export const judgePoint = ({tier, prev, next, linkPoint, tried = []}) => {
    if (tier !== 'guest' || !next || !next.x || !next.y) return {allow:true};
    if (samePoint(next, prev) || samePoint(next, linkPoint)) return {allow:true};
    const key = pointKey(next);
    if (tried.includes(key)) return {allow:true};
    if (tried.length >= FREE_SEARCHES) return {allow:false, prompt:{kind:'search'}};
    return {allow:true, tried:[...tried, key]};
};
